import React from "react";
import qs from "qs";
import { useReportsQuery, useRunReportMutation } from "@queries/reports";
import Loader from "@components/Loader";
import api from "@services/api";
import "@styles/opencart-admin.css";

function buildInitialParams(report) {
  if (!report) return {};
  const values = {};
  (report.params || []).forEach((p) => {
    if (p.default !== undefined && p.default !== null) {
      values[p.name] = p.default;
    } else if (p.type === "select") {
      const optionKeys = Object.keys(p.options || {});
      values[p.name] = optionKeys.length ? optionKeys[0] : "";
    } else {
      values[p.name] = "";
    }
  });
  return values;
}

function renderParamInput(param, value, onChange) {
  const commonProps = {
    className: "oc-input",
    value: value ?? "",
    onChange: (e) => onChange(param.name, e.target.value)
  };

  if (param.type === "date") {
    return <input type="date" {...commonProps} />;
  }

  if (param.type === "datetime") {
    return <input type="datetime-local" {...commonProps} />;
  }

  if (param.type === "integer") {
    return <input type="number" {...commonProps} />;
  }

  if (param.type === "boolean") {
    return (
      <select
        className="oc-input"
        value={value === "" ? "" : value ? "1" : "0"}
        onChange={(e) =>
          onChange(
            param.name,
            e.target.value === "" ? "" : e.target.value === "1"
          )
        }
      >
        <option value="">Any</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </select>
    );
  }

  if (param.type === "select") {
    return (
      <select
        className="oc-input"
        value={value ?? ""}
        onChange={(e) => onChange(param.name, e.target.value)}
      >
        {Object.entries(param.options || {}).map(([val, label]) => (
          <option key={val} value={val}>
            {label}
          </option>
        ))}
      </select>
    );
  }

  return <input type="text" {...commonProps} />;
}

export default function ReportsPage() {
  const { data, isLoading, isFetching, isError, error } = useReportsQuery();
  const runMutation = useRunReportMutation();

  const reports = data?.data || [];

  const [selectedKey, setSelectedKey] = React.useState("");
  const [params, setParams] = React.useState({});
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (!selectedKey && reports.length > 0) {
      setSelectedKey(reports[0].key);
    }
  }, [reports, selectedKey]);

  const selectedReport = React.useMemo(
    () => reports.find((r) => r.key === selectedKey),
    [reports, selectedKey]
  );

  React.useEffect(() => {
    setResult(null);
    setParams(buildInitialParams(selectedReport));
  }, [selectedReport]);

  function handleParamChange(name, value) {
    setParams((prev) => ({ ...prev, [name]: value }));
  }

  async function handleRun(e) {
    e.preventDefault();
    if (!selectedReport) return;
    const cleanParams = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v !== undefined && v !== null && v !== "") {
        cleanParams[k] = v;
      }
    });
    const data = await runMutation.mutateAsync({
      key: selectedReport.key,
      params: cleanParams
    });
    setResult(data);
  }

  function handleExportCsv(e) {
    e.preventDefault();
    if (!result || !result.columns || !result.rows) return;

    const headers = result.columns;
    
    const escapeCsv = (v) => {
      const s = String(v ?? "");
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [];
    lines.push(headers.map(escapeCsv).join(","));

    for (const row of result.rows) {
      const rowData = headers.map(col => escapeCsv(row[col]));
      lines.push(rowData.join(","));
    }

    const csv = lines.join("\n");
    // Add BOM for Excel to properly recognize UTF-8 (Arabic characters)
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedReport?.title || "report"}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const isBusy = isLoading || isFetching || runMutation.isLoading;

  return (
    <div className="oc-content">
      {isBusy && <Loader />}

      <div className="oc-header">
        <h1>Reports</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Reports</span>
        </nav>
      </div>

      <div className="oc-card" style={{ marginBottom: 16 }}>
        <div className="oc-toolbar">
          <div>
            <strong>Choose the report type</strong>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              className="oc-input"
              style={{ minWidth: 260 }}
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
            >
              {reports.map((rep) => (
                <option key={rep.key} value={rep.key}>
                  {rep.title}
                </option>
              ))}
            </select>
          </div>
        </div>
        {selectedReport && selectedReport.description && (
          <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
            {selectedReport.description}
          </div>
        )}
      </div>

      {isError && (
        <div className="oc-card" style={{ color: "#c00", marginBottom: 12 }}>
          Error: {error?.message || "Unknown error"}
        </div>
      )}

      <div
        className="oc-card"
        style={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 16 }}
      >
        <div>
          <div className="oc-toolbar" style={{ marginBottom: 12 }}>
            <div>
              <strong>
                {selectedReport ? selectedReport.title : "Report result"}
              </strong>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {result && result.rows.length > 0 && (
                <button
                  className="oc-btn ghost"
                  onClick={handleExportCsv}
                >
                  Export CSV
                </button>
              )}
              <button
                className="oc-btn primary"
                onClick={handleRun}
                disabled={!selectedReport || runMutation.isLoading}
              >
                {runMutation.isLoading ? "Running..." : "Run report"}
              </button>
            </div>
          </div>

          <table className="oc-table">
            <thead>
              <tr>
                {result?.columns?.length
                  ? result.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))
                  : null}
              </tr>
            </thead>
            <tbody>
              {!result && (
                <tr>
                  <td
                    colSpan={result?.columns?.length || 1}
                    style={{ textAlign: "center", padding: 24 }}
                  >
                    Run the report to see data.
                  </td>
                </tr>
              )}
              {result && result.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={result.columns.length || 1}
                    style={{ textAlign: "center", padding: 24 }}
                  >
                    No data for the selected filters.
                  </td>
                </tr>
              )}
              {result &&
                result.rows.map((row, idx) => (
                  <tr key={idx}>
                    {result.columns.map((col) => (
                      <td key={col}>{row[col]}</td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>

          {result && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: "#666",
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <div>
                Rows: <strong>{result.meta?.rows ?? 0}</strong>
              </div>
              <div>
                Max rows: <strong>{result.meta?.max_rows ?? 0}</strong>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="title" style={{ marginBottom: 8 }}>
            Filter
          </div>
          {selectedReport ? (
            <form onSubmit={handleRun}>
              {(selectedReport.params || []).map((param) => (
                <div className="form-group" key={param.name}>
                  <label>
                    {param.label}
                    {param.required && (
                      <span style={{ color: "#c00", marginLeft: 4 }}>*</span>
                    )}
                  </label>
                  {renderParamInput(
                    param,
                    params[param.name],
                    handleParamChange
                  )}
                </div>
              ))}

              <button
                type="submit"
                className="oc-btn primary"
                style={{ marginTop: 8, width: "100%" }}
                disabled={runMutation.isLoading}
              >
                {runMutation.isLoading ? "Running..." : "Filter"}
              </button>
            </form>
          ) : (
            <div style={{ fontSize: 13, color: "#666" }}>
              Select a report to configure filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

